import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Link,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { EvaluationMetricTable } from "components/EvaluationMetric";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { ChangeEvent, FC, useMemo, useState } from "react";
import { RequestFeedbackData } from "../types";
import { SKIP_REVIEW_EPA_SUMMARY_STORAGE_KEY, STEPS } from "../config";
import { REQUIREMENT } from "helpers/constants";
import { SuccessRubric } from "modals/SuccessRubric";

type PerformProps = {
  onUpdate: (data: any) => void;
  data: RequestFeedbackData;
  onBegin: () => void;
  handleStep: (step: string, action?: string) => void;
  customLabels: any[];
  isCoachInitiated?: boolean;
};

export const Perform: FC<PerformProps> = ({
  onUpdate,
  data,
  onBegin,
  handleStep,
  customLabels,
  isCoachInitiated = false,
}) => {
  const { selectedCompetencySkill: requirement } = data;
  const [selectedSubRequirement, setSelectedSubRequirement] = useState<any>(null);
  const [openSuccessRubric, setOpenSuccessRubric] = useState(false);
  const [skipReviewEpaSummary, setSkipReviewEpaSummary] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return (
      window.localStorage.getItem(SKIP_REVIEW_EPA_SUMMARY_STORAGE_KEY) === "true"
    );
  });

  const isITER = useMemo(
    () => requirement?.type === REQUIREMENT.REQUIREMENT_ITER,
    [requirement?.type],
  );

  const evaluationMetrics = useMemo(() => {
    return requirement?.evaluation_metrics ?? [];
  }, [requirement]);

  const subRequirements = useMemo(() => {
    if (isITER && requirement?.competencies) {
      return requirement.competencies
        .map((c: any) => c.competency)
        .filter(Boolean);
    }
    return [];
  }, [isITER, requirement]);

  if (!requirement) {
    return <Box>No requirement selected</Box>;
  }

  const title = isITER ? "Review ITER Summary" : "Review EPA Summary";
  const backStep = isITER ? STEPS.REQUEST2 : STEPS.REQUEST1;

  const handleOpenRubric = (subReq: any) => {
    setSelectedSubRequirement(subReq);
    setOpenSuccessRubric(true);
  };

  const handleSkipReviewEpaSummaryChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const shouldSkip = event.target.checked;
    setSkipReviewEpaSummary(shouldSkip);

    if (typeof window === "undefined") return;
    if (shouldSkip) {
      window.localStorage.setItem(SKIP_REVIEW_EPA_SUMMARY_STORAGE_KEY, "true");
      return;
    }
    window.localStorage.removeItem(SKIP_REVIEW_EPA_SUMMARY_STORAGE_KEY);
  };

  return (
    <>
      <StepLayoutContainer
        title={title}
        onClickNext={onBegin}
        onClickBack={() => handleStep(backStep, "back")}
        marginXSBottom={8.5}
      >
        {isITER && subRequirements.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Competencies
            </Typography>
            <List>
              {subRequirements.map((subReq: any) => (
                <ListItem
                  key={subReq.requirement_id}
                  sx={{ flexDirection: "column", alignItems: "flex-start" }}
                >
                  <ListItemText
                    primary={subReq.name}
                    secondary={subReq.description}
                  />
                  <Box mt={1} width="100%">
                    <Typography variant="body2">
                      {isCoachInitiated
                        ? "Need a reminder? Review the goals and success rubric of the requirement you selected to evaluate. "
                        : "Need a reminder? Review the goals and success rubric of the requirement you selected to perform. "}
                      <Link
                        onClick={() => handleOpenRubric(subReq)}
                        sx={{ cursor: "pointer" }}
                      >
                        See Success Rubric
                      </Link>
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {!isITER && (
          <Box>
            <EvaluationMetricTable
              requirement={data?.selectedCompetencySkill}
              evaluationMetrics={evaluationMetrics || []}
              customLabels={customLabels}
            />
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipReviewEpaSummary}
                    onChange={handleSkipReviewEpaSummaryChange}
                  />
                }
                label="Skip this Review EPA Summary step next time"
              />
            </Box>
          </Box>
        )}
      </StepLayoutContainer>
      {openSuccessRubric && selectedSubRequirement && (
        <SuccessRubric
          open={openSuccessRubric}
          onClose={() => {
            setOpenSuccessRubric(false);
            setSelectedSubRequirement(null);
          }}
          requirement={selectedSubRequirement}
          customLabels={customLabels}
        />
      )}
    </>
  );
};
